/**
 * aguardarIndexacao — iniciar a execução esperando o contexto ser vetorizado.
 *
 * POST /executions devolve 409 com `codigo: 'contexto_nao_indexado'` e
 * `pendentes: N` quando a memória do projeto ainda está sem vetor. No mesmo
 * movimento a API dispara o dreno (EmbedPendingWorkflow), que costuma zerar em
 * segundos. Aqui a tela repete a tentativa sozinha, expondo o estado da espera,
 * até a API aceitar — ou até o limite, quando a espera vira mensagem clara.
 *
 * Defeito de campo (2026-09-24): sem isso o 409 sumia e o usuário achava que o
 * botão "Executar" não funcionava.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export const CODIGO_CONTEXTO_NAO_INDEXADO = 'contexto_nao_indexado';
export const CODIGO_INDEXACAO_ESGOTADA = 'indexacao_esgotada';

const OCIOSO = Object.freeze({ fase: 'ocioso', pendentes: 0, alvo: null });

/**
 * Nº de itens pendentes se o erro for o 409 de contexto não indexado; senão null.
 * Aceita o corpo estruturado e, por compatibilidade, o texto da API antiga.
 */
/** "2 minutos", "1 minuto", "30 segundos" — duração legível do limite de espera. */
export function duracaoLegivel(ms) {
  const seg = Math.max(0, Math.round(Number(ms) / 1000));
  if (seg < 60) return `${seg} ${seg === 1 ? 'segundo' : 'segundos'}`;
  const min = Math.round(seg / 60);
  return `${min} ${min === 1 ? 'minuto' : 'minutos'}`;
}

export function pendentesDoContexto(err) {
  if (!err || err.status !== 409) return null;
  const corpo = err.problemBody ?? {};
  if (corpo.codigo === CODIGO_CONTEXTO_NAO_INDEXADO) {
    const n = Number(corpo.pendentes);
    return Number.isFinite(n) ? n : 0;
  }
  const m = /sendo indexado:\s*(\d+)/i.exec(String(err.detail ?? ''));
  return m ? Number(m[1]) : null;
}

/**
 * Envolve uma função de início (ex.: `startExec.mutateAsync`) com a espera.
 *
 * @param {(alvo: any) => Promise<any>} iniciarFn
 * @param {{ intervaloMs?: number, limiteMs?: number }} [opcoes]
 * @returns {{ iniciar: (alvo:any)=>Promise<any>, estado: {fase:'ocioso'|'indexando'|'esgotado', pendentes:number, alvo:any, tentativas?:number, limiteMs?:number} }}
 *   `iniciar` resolve com o resultado da API; rejeita com o erro original nos
 *   demais casos, e com `{codigo: 'indexacao_esgotada'}` ao estourar o limite.
 */
export function useIniciarAguardandoIndexacao(iniciarFn, { intervaloMs = 4000, limiteMs = 120000 } = {}) {
  const [estado, setEstado] = useState(OCIOSO);
  const montado = useRef(true);
  const fnRef = useRef(iniciarFn);
  fnRef.current = iniciarFn;

  useEffect(() => {
    montado.current = true;
    return () => { montado.current = false; };
  }, []);

  const iniciar = useCallback(async (alvo) => {
    const inicio = Date.now();
    let tentativas = 0;
    for (;;) {
      tentativas += 1;
      try {
        const res = await fnRef.current(alvo);
        if (montado.current) setEstado(OCIOSO);
        return res;
      } catch (err) {
        const pendentes = pendentesDoContexto(err);
        if (pendentes === null) {
          if (montado.current) setEstado(OCIOSO);
          throw err;
        }
        if (!montado.current) {
          throw Object.assign(new Error('Espera da indexação interrompida.'), { codigo: 'cancelado' });
        }
        if (Date.now() - inicio + intervaloMs > limiteMs) {
          setEstado({ fase: 'esgotado', pendentes, alvo, tentativas, limiteMs });
          throw Object.assign(
            new Error(`A indexação do contexto não terminou a tempo (${pendentes} itens pendentes).`),
            { codigo: CODIGO_INDEXACAO_ESGOTADA, pendentes },
          );
        }
        setEstado({ fase: 'indexando', pendentes, alvo, tentativas });
        await new Promise((r) => setTimeout(r, intervaloMs));
        if (!montado.current) {
          throw Object.assign(new Error('Espera da indexação interrompida.'), { codigo: 'cancelado' });
        }
      }
    }
  }, [intervaloMs, limiteMs]);

  return { iniciar, estado };
}
